import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import PhoneIcon from "@mui/icons-material/Phone";
import NoteIcon from "@mui/icons-material/Note";
import CodeIcon from "@mui/icons-material/Code";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import HomeIcon from "@mui/icons-material/Home";
import { register } from "../../../Services/apiService";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import logo from "../../../Assets/logo.png";

const Signup = () => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, "يجب أن يتكون الاسم من 3 أحرف على الأقل")
      .required("الاسم مطلوب"),
    email: Yup.string()
      .email("تنسيق البريد الإلكتروني غير صحيح")
      .required("البريد الإلكتروني مطلوب"),
    password: Yup.string()
      .min(6, "يجب أن تتكون كلمة المرور من 6 أحرف على الأقل")
      .required("كلمة المرور مطلوبة"),
    phone: Yup.string()
      .matches(/^\d{10,}$/, "يجب أن يحتوي رقم الهاتف على 10 أرقام على الأقل")
      .required("رقم الهاتف مطلوب"),
    note: Yup.string().max(200, "لا يمكن أن تتجاوز الملاحظة 200 حرف"),
    user_broker_code: Yup.string().max(50, "كود الوسيط لا يمكن أن يتجاوز 50 حرف"),
  });

  const formik = useFormik({
    initialValues: { name: "", email: "", password: "", phone: "", note: "", user_broker_code: "" },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload = { ...values };
        
        console.log("Submitting payload:", payload);
        
        const response = await register(payload);

        console.log("Registration response:", response);

        if (response.data.data) {
          const { id, verification_code, email } = response.data.data;
          setSuccess("تم إنشاء الحساب بنجاح! يرجى التحقق من بريدك الإلكتروني. 🎉");
          setError("");

          setTimeout(() => {
            navigate(`/verifi-page?email=${encodeURIComponent(email)}&id=${encodeURIComponent(id)}`);
          }, 1500);
        } else {
          setError(response.data.message || "حدث خطأ أثناء إنشاء الحساب.");
          setSuccess("");
        }
      } catch (err) {
        console.error("Registration error:", err);
        console.error("Error response data:", err.response?.data);
        
        let errorMessage = "حدث خطأ أثناء الاتصال بالسيرفر.";
        
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.response?.data?.errors) {
          const errors = err.response.data.errors;
          errorMessage = Object.values(errors).flat().join(", ");
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        setSuccess("");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)",
        position: "relative",
        py: 6,
        px: 2,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "radial-gradient(circle, rgba(243, 111, 33, 0.03) 1px, transparent 1px)",
          backgroundSize: "30px 30px",
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        {/* زر العودة للصفحة الرئيسية */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Button
            component={RouterLink}
            to="/"
            variant="outlined"
            startIcon={<HomeIcon />}
            sx={{
              borderRadius: 3,
              borderWidth: 2,
              borderColor: "#f36f21",
              color: "#f36f21",
              fontWeight: 600,
              px: 3,
              py: 1,
              textTransform: "none",
              "&:hover": {
                borderWidth: 2,
                borderColor: "#e65100",
                background: "rgba(243, 111, 33, 0.05)",
                transform: "translateY(-2px)",
              },
            }}
          >
            العودة للصفحة الرئيسية
          </Button>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 5 },
            borderRadius: 5,
            border: "2px solid #e8e8e8",
            background: "white",
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0 12px 48px rgba(243, 111, 33, 0.12)",
              borderColor: "#f36f21",
            },
          }}
        >
          {/* Logo & App Name */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{
                height: { xs: 70, md: 90 },
                width: "auto",
                mb: 2,
                filter: "drop-shadow(0 4px 8px rgba(243, 111, 33, 0.2))",
              }}
            />
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                textAlign: "center",
                fontSize: { xs: "1.5rem", md: "2rem" },
              }}
            >
              ريستو كاش
            </Typography>
          </Box>

          {/* Icon Header - تم تصغيره */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: "linear-gradient(135deg, rgba(243, 111, 33, 0.1) 0%, rgba(230, 81, 0, 0.1) 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HowToRegIcon sx={{ fontSize: 30, color: "#f36f21" }} />
            </Box>
          </Box>

          <Typography
            variant="h5"
            align="center"
            gutterBottom
            fontWeight={800}
            sx={{
              color: "text.primary",
              mb: 1,
            }}
          >
            إنشاء حساب جديد
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{ mb: 3, color: "text.secondary", lineHeight: 1.8 }}
          >
            انضم إلينا وابدأ رحلتك في إدارة مطعمك باحترافية
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "error.light",
              }}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              sx={{
                mb: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "success.light",
              }}
            >
              {success}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="الاسم الكامل"
                  name="name"
                  fullWidth
                  {...formik.getFieldProps("name")}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: "#f36f21" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      "&:hover fieldset": { borderColor: "#f36f21" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#f36f21",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="البريد الإلكتروني"
                  name="email"
                  type="email"
                  fullWidth
                  {...formik.getFieldProps("email")}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: "#f36f21" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      "&:hover fieldset": { borderColor: "#f36f21" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#f36f21",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="كلمة المرور"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  {...formik.getFieldProps("password")}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "#f36f21" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      "&:hover fieldset": { borderColor: "#f36f21" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#f36f21",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="رقم الهاتف"
                  name="phone"
                  fullWidth
                  {...formik.getFieldProps("phone")}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={{ color: "#f36f21" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      "&:hover fieldset": { borderColor: "#f36f21" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#f36f21",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="ملاحظة (اختيارية)"
                  name="note"
                  fullWidth
                  multiline
                  rows={3}
                  {...formik.getFieldProps("note")}
                  error={formik.touched.note && Boolean(formik.errors.note)}
                  helperText={formik.touched.note && formik.errors.note}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 2 }}>
                        <NoteIcon sx={{ color: "#f36f21" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      "&:hover fieldset": { borderColor: "#f36f21" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#f36f21",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="كود الوسيط (اختياري)"
                  name="user_broker_code"
                  fullWidth
                  {...formik.getFieldProps("user_broker_code")}
                  error={formik.touched.user_broker_code && Boolean(formik.errors.user_broker_code)}
                  helperText={formik.touched.user_broker_code && formik.errors.user_broker_code}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CodeIcon sx={{ color: "#f36f21" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      "&:hover fieldset": { borderColor: "#f36f21" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#f36f21",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <HowToRegIcon />}
                  sx={{
                    py: 2,
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    borderRadius: 3,
                    textTransform: "none",
                    background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                    boxShadow: "0 6px 20px rgba(243, 111, 33, 0.3)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #e65100 0%, #d84315 100%)",
                      boxShadow: "0 8px 28px rgba(243, 111, 33, 0.4)",
                      transform: "translateY(-2px)",
                    },
                    "&:disabled": {
                      background: "rgba(243, 111, 33, 0.5)",
                    },
                  }}
                >
                  {loading ? "جاري إنشاء الحساب..." : "إنشاء الحساب"}
                </Button>
              </Grid>
            </Grid>
          </form>

          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              لديك حساب بالفعل؟{" "}
              <Box
                component={RouterLink}
                to="/login"
                sx={{
                  color: "#f36f21",
                  fontWeight: 700,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                تسجيل الدخول
              </Box>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Signup;
